import { useEffect } from 'react';

import cn from 'classnames';
import { FieldTypes } from 'constants/field-types-enum';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'components/FormComponents';
import { FormSectionEdit } from 'components/SectionedForm/FormSectionEdit/FormSectionEdit';

import { messagePopUp } from 'api/app-slice';
import {
  getCustomFields,
  selectCustomFields,
  updateCustomFields,
} from 'api/candidate-custom-fields-slice';
import { selectCompany } from 'api/company-slice';

const RENEWAL_FIELD_SCOPE_OPTIONS = [
  { code: 'applies_to_candidates', type: 'bool', title: 'Applies to candidates' },
  { code: 'applies_to_employees', type: 'bool', title: 'Applies to employees' },
];

export const CustomFields = ({ setLoading = () => {} }) => {
  const dispatch = useDispatch();

  const customFields = useSelector(selectCustomFields);
  const company = useSelector(selectCompany);
  const renewalDashboardEnabled = company?.renewal_dashboard_enabled;
  const extraOptions = renewalDashboardEnabled ? RENEWAL_FIELD_SCOPE_OPTIONS : null;

  const {
    control,
    register,
    setValue,
    unregister,
    reset,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { fields, append, move, replace } = useFieldArray({
    control,
    name: 'fields',
    keyName: 'key',
  });

  // Initialize
  useEffect(() => {
    setLoading(true);
    dispatch(getCustomFields()).then(result => setLoading(false));
  }, []);

  // Update fields
  useEffect(() => {
    const sortedFields = [...(customFields || [])]
      .sort((a, b) => a.order - b.order)
      .map((field, index) => ({ ...field, order: index + 1 }));
    reset({ fields: sortedFields });
  }, [customFields?.length]);

  const onSave = async data => {
    setLoading(true);
    try {
      const { payload: response } = await dispatch(updateCustomFields(data));
      if (response.error) throw new Error(response.error.message);
      dispatch(
        messagePopUp({
          text: 'Custom Fields successfully saved',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    } catch (error) {
      dispatch(messagePopUp({ text: error, state: MESSAGE_STATE_ERROR, hide: true }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveField = index => {
    const currentFields = getValues('fields') || [];
    replace(
      fields
        .map((field, i) => ({ ...field, ...(currentFields[i] || {}) }))
        .filter((_, i) => i !== index)
        .map((field, i) => ({ ...field, order: i + 1 })),
    );
  };

  const addField = () => {
    const extraOptionsDefaults = (extraOptions || []).reduce(
      (defaults, option) => ({ ...defaults, [option.code]: true }),
      {},
    );

    append({
      order: fields.length + 1,
      text: '',
      type: FieldTypes.FreeText,
      required: true,
      ...extraOptionsDefaults,
      options: { required: true, options: [{ value: '' }] },
    });
  };

  return (
    <form className={cn('u-flex-box', 'u-flex-justify-center')} onSubmit={handleSubmit(onSave)}>
      <div className={cn('card', 'card-with-border', 'card--medium')}>
        <div className="u-margin--large">
          <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
            <h1 className="title-2">Custom Fields</h1>
          </div>
          <p className="t-small">
            Create and manage custom fields to collect additional information about each candidate
            {renewalDashboardEnabled && ' and/or employee'}.
          </p>
        </div>
        <div className={cn('background-secondary', 'u-padding', 'card_border--top')}>
          <div className={cn('card', 'card-with-border', 'u-padding')}>
            <FormSectionEdit
              fields={fields}
              control={control}
              errors={errors}
              customField={true}
              extraOptions={extraOptions}
              unregister={unregister}
              watch={watch}
              register={register}
              setValue={setValue}
              moveField={move}
              addField={addField}
              removeField={handleRemoveField}
            />
          </div>
        </div>
        <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-end')}>
          <Button submit>Save</Button>
        </div>
      </div>
    </form>
  );
};
